import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import useClientComments from '@ubeya/shared/hooks/account/useClientComments';
import { Button as BaseButton, Container, Loader, TextArea as BaseTextArea } from '@ubeya/shared-web/components';
import { SORT_BY_DATE_AND_TIME } from '@ubeya/shared/utils/sorting';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import Comment from './Comment';

const Wrapper = styled.div``;

const Title = styled.h2`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
`;

const TextArea = styled(BaseTextArea)`
  flex: 1;
`;

const NewTextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const Button = styled(BaseButton)`
  height: 40px;
  width: 40px;
  margin-left: 10px;
  display: flex;
  padding: 0;
  justify-content: center;
`;

const ClientComments = ({ clientId }) => {
  const { t } = useContext(ConfigContext);
  const [isSending, setIsSending] = useState(false);
  const { dateFormat, timeFormat } = useContext(ConfigContext);
  const [newCommentText, setNewCommentText] = useState();
  const onChangeNewCommentText = useCallback((val) => setNewCommentText(val), []);
  const onSubmitNewCommentText = useCallback((e) => e.which === 13 && setNewCommentText(), []);

  const { isLoading, data, deleteComment, addComment } = useClientComments({
    clientId
  });

  const timeSorter = SORT_BY_DATE_AND_TIME('createdAt');

  const sortedData = useMemo(() => data.sort((a, b) => timeSorter(b, a)), [data, timeSorter]);

  const onAddComment = useCallback(
    async (event) => {
      event.preventDefault();
      if (!newCommentText) {
        return;
      }

      setIsSending(true);

      await addComment({ comment: newCommentText });
      setNewCommentText('');
      setIsSending(false);
    },
    [addComment, newCommentText]
  );

  return (
    <Container>
      <Title>{t('comments')}</Title>

      <NewTextContainer>
        <TextArea
          placeholder={t('anyComments')}
          value={newCommentText}
          onChange={onChangeNewCommentText}
          onKeyPress={onSubmitNewCommentText}
        />
        <Button primary narrow onClick={onAddComment} isLoading={isSending}>
          +
        </Button>
      </NewTextContainer>

      <Wrapper>
        {(sortedData || []).map(({ id, comment, admin, createdAt }) => (
          <Comment
            key={id}
            id={id}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            t={t}
            comment={comment}
            createdAt={createdAt}
            firstName={admin?.firstName || ''}
            lastName={admin?.lastName || ''}
            deleteComment={deleteComment}
            addComment={addComment}
          />
        ))}
      </Wrapper>

      {isLoading && <Loader />}
    </Container>
  );
};

export default React.memo(ClientComments);
